import apiService from './api.service';

// Types
export interface AWSCredentials {
    accessKeyId: string;
    secretAccessKey: string;
    region: string;
    sessionToken?: string;
}

export interface AzureCredentials {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    subscriptionId: string;
}

export interface GCPCredentials {
    projectId: string;
    clientEmail: string;
    privateKey: string;
}

export type CloudCredentials = AWSCredentials | AzureCredentials | GCPCredentials;

export type CloudProvider = 'aws' | 'azure' | 'gcp';
export type ScanType = 'full' | 'security' | 'cost' | 'finops';
export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';
export type FinOpsCategory = 'reserved-instances' | 'right-sizing' | 'storage-optimization' | 'idle-resources' | 'cost-anomaly';

export interface SecurityFinding {
    id: string;
    title: string;
    description: string;
    severity: Severity;
    category: string;
    resource: string;
    resourceType: string;
    region?: string;
    recommendation: string;
    metadata?: Record<string, unknown>;
}

export interface CostRecommendation {
    id: string;
    title: string;
    description: string;
    category: string;
    resource: string;
    resourceType: string;
    region?: string;
    estimatedMonthlySavings?: number;
    recommendation: string;
    metadata?: Record<string, unknown>;
}

export interface FinOpsRecommendation {
    id?: string;
    type: string;
    category: FinOpsCategory;
    severity: Severity;
    resource: string;
    title: string;
    description: string;
    currentMonthlyCost: number;
    estimatedMonthlySavings: number;
    estimatedAnnualSavings: number;
    savingsPercentage: number;
    recommendation: string;
    implementationEffort: 'low' | 'medium' | 'high';
    riskLevel: 'low' | 'medium' | 'high';
    metadata?: Record<string, unknown>;
}

export interface CostHistoryItem {
    month: string;
    cost: number;
    currency: string;
}

export interface TopServiceCost {
    service: string;
    cost: number;
}

export interface FinOpsSummary {
    totalCurrentMonthlyCost: number;
    totalEstimatedMonthlySavings: number;
    totalEstimatedAnnualSavings: number;
    savingsPercentage: number;
    recommendationCount: number;
    byCategory: {
        reservedInstances: { count: number; savings: number };
        rightSizing: { count: number; savings: number };
        storageOptimization: { count: number; savings: number };
        idleResources: { count: number; savings: number };
        costAnomalies: { count: number; savings: number };
    };
    topOpportunities: FinOpsRecommendation[];
    // Cost history data (6 months)
    lastMonthCost?: number;
    costTrend?: number;
    costHistory?: CostHistoryItem[];
    topServices?: TopServiceCost[];
}

export interface ScanReport {
    summary: {
        totalSecurityFindings: number;
        totalCostRecommendations: number;
        estimatedTotalMonthlySavings: number;
        estimatedMonthlySavings?: number;
        severityBreakdown?: Record<Severity, number>;
    };
    severityBreakdown?: Record<Severity, number>;
    categoryBreakdown?: Record<string, number>;
    topSecurityIssues?: SecurityFinding[];
    topCostSavings?: CostRecommendation[];
}

export interface ScanResult {
    scanId?: string;
    provider: CloudProvider;
    scanType: ScanType;
    timestamp: string;
    duration: number;
    security?: {
        findings: SecurityFinding[];
        scannedAt?: string;
        scanDuration?: number;
        findingsCount?: number;
    };
    cost?: {
        recommendations: CostRecommendation[];
        scannedAt?: string;
        scanDuration?: number;
        recommendationsCount?: number;
    };
    finops?: {
        recommendations: FinOpsRecommendation[];
        summary?: FinOpsSummary;
    };
    report?: ScanReport;
    errors?: string[];
    isPaid?: boolean;
    hasPremiumAccess?: boolean;
    effectiveIsPaid?: boolean;
}

export interface ProviderInfo {
    id: CloudProvider;
    name: string;
    requiredCredentials: string[];
}

export interface ProvidersResponse {
    success: boolean;
    data: {
        providers: ProviderInfo[];
        scanTypes: ScanType[];
    };
}

export interface ValidateCredentialsRequest {
    provider: CloudProvider;
    credentials: CloudCredentials;
}

export interface ValidateCredentialsResponse {
    success: boolean;
    data: {
        valid: boolean;
        provider: CloudProvider;
        message: string;
        details?: Record<string, unknown>;
    };
}

export interface StartScanRequest {
    provider: CloudProvider;
    credentials: CloudCredentials;
    scanType?: ScanType;
    callbackUrl?: string;
}

export interface StartScanResponse {
    success: boolean;
    message?: string;
    data?: ScanResult;
    scanId: string;
    status?: 'pending' | 'running' | 'completed' | 'failed';
    provider?: CloudProvider;
    scanType?: ScanType;
    isPaid?: boolean;
    hasPremiumAccess?: boolean;
}

export interface ScanHistoryItem {
    id: string;
    provider: CloudProvider;
    scanType: ScanType;
    status: 'pending' | 'running' | 'completed' | 'failed';
    isPaid: boolean;
    totalSecurityFindings: number;
    totalCostRecommendations: number;
    criticalIssues: number;
    estimatedMonthlySavings: number;
    durationMs: number;
    createdAt: string;
    completedAt?: string;
}

export interface ScanHistoryResponse {
    success: boolean;
    data: {
        scans: ScanHistoryItem[];
        total: number;
        limit: number;
        offset: number;
    };
}

export interface GetScanResponse {
    success: boolean;
    data: ScanResult & { hasPremiumAccess?: boolean; effectiveIsPaid?: boolean };
    scanId: string;
    isPaid: boolean;
    hasPremiumAccess?: boolean;
    effectiveIsPaid?: boolean;
}

export interface PurchaseScanResponse {
    success: boolean;
    message: string;
    data: ScanResult;
    scanId: string;
    isPaid: boolean;
}

class CloudScanApi {
    /**
     * Get available cloud providers and scan types
     */
    async getProviders(): Promise<ProvidersResponse> {
        return apiService.get('/v1/cloud-scan/providers');
    }

    /**
     * Validate cloud provider credentials
     */
    async validateCredentials(
        request: ValidateCredentialsRequest
    ): Promise<ValidateCredentialsResponse> {
        // Build the payload with provider-specific credential field
        const payload: Record<string, unknown> = {
            provider: request.provider,
        };

        // Map credentials to the provider-specific field expected by the backend
        switch (request.provider) {
            case 'aws':
                payload.awsCredentials = request.credentials;
                break;
            case 'azure':
                payload.azureCredentials = request.credentials;
                break;
            case 'gcp':
                payload.gcpCredentials = request.credentials;
                break;
        }

        return apiService.post('/v1/cloud-scan/validate', payload);
    }

    /**
     * Start a cloud security and cost scan
     */
    async startScan(request: StartScanRequest): Promise<StartScanResponse> {
        // Build the payload with provider-specific credential field
        const payload: Record<string, unknown> = {
            provider: request.provider,
            scanType: request.scanType,
            callbackUrl: request.callbackUrl,
        };

        // Map credentials to the provider-specific field expected by the backend
        switch (request.provider) {
            case 'aws':
                payload.awsCredentials = request.credentials;
                break;
            case 'azure':
                payload.azureCredentials = request.credentials;
                break;
            case 'gcp':
                payload.gcpCredentials = request.credentials;
                break;
        }

        return apiService.post('/v1/cloud-scan/scans', payload);
    }

    /**
     * Get user's scan history
     */
    async getScanHistory(params?: { limit?: number; offset?: number }): Promise<ScanHistoryResponse> {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.offset) queryParams.append('offset', params.offset.toString());
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
        return apiService.get(`/v1/cloud-scan/history${query}`);
    }

    /**
     * Get a specific scan by ID
     */
    async getScan(scanId: string): Promise<GetScanResponse> {
        return apiService.get(`/v1/cloud-scan/scans/${scanId}`);
    }

    /**
     * Get scan usage info (monthly limits and usage)
     */
    async getScanUsageInfo(): Promise<{
        success: boolean;
        data: {
            tier: string;
            monthlyLimit: number;
            monthlyUsed: number;
            monthlyRemaining: number;
            canMakeFreeScan: boolean;
        };
    }> {
        return apiService.get('/v1/cloud-scan/usage');
    }

    /**
     * Purchase full report for a scan
     */
    async purchaseScan(scanId: string): Promise<PurchaseScanResponse> {
        return apiService.post(`/v1/cloud-scan/scans/${scanId}/purchase`, {});
    }

    /**
     * Get scan details (admin only - includes full report)
     */
    async getAdminScanDetails(scanId: string): Promise<GetScanResponse & { data: ScanResult & { user?: any; tenant?: any } }> {
        return apiService.get(`/v1/cloud-scan/admin/scans/${scanId}`);
    }

    /**
     * Get all scans (admin only)
     */
    async getAdminScans(params?: {
        limit?: number;
        offset?: number;
        provider?: string;
        status?: string;
        isPaid?: string;
    }): Promise<{
        success: boolean;
        data: {
            scans: any[];
            total: number;
        };
    }> {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.offset) queryParams.append('offset', params.offset.toString());
        if (params?.provider) queryParams.append('provider', params.provider);
        if (params?.status) queryParams.append('status', params.status);
        if (params?.isPaid) queryParams.append('isPaid', params.isPaid);
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
        return apiService.get(`/v1/cloud-scan/admin/scans${query}`);
    }

    /**
     * Get admin statistics
     */
    async getAdminStats(): Promise<{
        success: boolean;
        data: {
            totalScans: number;
            totalPaid: number;
            totalRevenue: number;
            averageDuration: number;
            scansByProvider: Record<string, number>;
            scansByStatus: Record<string, number>;
        };
    }> {
        return apiService.get('/v1/cloud-scan/admin/stats');
    }

    /**
     * Delete a scan
     */
    async deleteScan(scanId: string): Promise<{ success: boolean; message: string }> {
        return apiService.delete(`/v1/cloud-scan/scans/${scanId}`);
    }
}

export const cloudScanApi = new CloudScanApi();
export default cloudScanApi;

